import {
  SESSION_FORMVALUES,
  SESSION_SERVICE_API_ERROR,
} from "../../../../constants/constant";
import {
  FormValues,
  SchedulerConfig,
} from "../../../../Interface/scheduler/form";
import { gardenAreaLookup } from "../../../../service/apiService";
import isInboundFromNbly from "../../../../utility/checkInBoundFromNbly";
import { sessionStorageUtils } from "../../../../utility/utility";
import { geocodeAddress } from "./util";

interface GardenAreaResponse {
  geojson?: Record<string, unknown>;
  status?: string;
  error?: unknown;
  data?: { geojson?: Record<string, unknown> };
}

const POLL_INTERVALS = [5000, 15000, 35000] as const; // 5s, 15s, 35s, 65s (total: 120s)
const redirectToConfirmationWithError = (
  formValues: FormValues,
  formConfig: SchedulerConfig,
): void => {
  const redirectionUrl = formConfig.redirectionPageUrl ?? "/confirmation";
  sessionStorageUtils.setItem(SESSION_FORMVALUES, formValues);
  sessionStorageUtils.setItem(SESSION_SERVICE_API_ERROR, true);
  const targetUrl = isInboundFromNbly()
    ? `${redirectionUrl}?leadconcept=${formConfig.conceptCode}`
    : redirectionUrl;

  globalThis.location.replace(targetUrl);
};

export const getPropertyLawnAreaCoordinates = async (
  formValues: FormValues,
  formConfig: SchedulerConfig,
) => {
  const { conceptID, city, state, zipcode, countryCode, address } = formValues;

  const coords = await geocodeAddress(address);
  if (!coords) {
    throw new Error("Failed to geocode address");
  }
  const [lat, long] = coords;
  if (!conceptID || !city || !state || !zipcode || !countryCode) {
    throw new Error("Missing required form values");
  }

  const addressWithoutComma = address.replace(/,/g, "");

  const payload = {
    vendorId: formConfig?.vendorId,
    conceptId: conceptID,
    lat: lat,
    long: long,
    street1: addressWithoutComma,
    street2: "",
    city: city,
    state: state,
    zip: zipcode,
    country: countryCode,
    requestedAreas: {
      frontLawn: true,
      backLawn: true,
      lawn: true,
    },
  };

  let data = await gardenAreaLookup(payload);
  // Polling mechanism for pending status
  const getStatusString = (
    response: GardenAreaResponse | { status?: string; error?: unknown },
  ): string => {
    if (typeof response?.status === "string") {
      return response.status.toLowerCase();
    }
    return String(response?.status || "").toLowerCase();
  };

  if (getStatusString(data) === "pending" && !data?.error) {
    for (const interval of POLL_INTERVALS) {
      await new Promise((resolve) => setTimeout(resolve, interval));
      data = await gardenAreaLookup(payload);
      if (getStatusString(data) !== "pending" || data?.error) {
        break;
      }
    }
  }

  if (getStatusString(data) === "failed") {
    redirectToConfirmationWithError(formValues, formConfig);
    return;
  }

  // Handle error or timeout scenarios
  if (data?.error || getStatusString(data) === "pending") {
    redirectToConfirmationWithError(formValues, formConfig);
    return;
  }

  if (
    !data?.data?.geojson ||
    (typeof data.data.geojson === "object" &&
      Object.keys(data.data.geojson).length === 0)
  ) {
    redirectToConfirmationWithError(formValues, formConfig);
    return;
  }
  return data?.data?.geojson ?? (data as GardenAreaResponse)?.geojson;
};
